import { useSEO } from '../../../utils/seo';
import Icon from '../../../components/base/Icon';

export default function FixFlipContractorBoiseIdaho() {
  useSEO({
    title: 'Fix & Flip Contractor Boise Idaho: Investor Guide',
    description: 'Complete guide for real estate investors choosing fix and flip contractors in Boise. Renovation scope, timelines, costs & investor partnerships in Treasure Valley.',
    keywords: 'fix and flip contractor Boise Idaho, real estate investor contractor Treasure Valley, house flip contractor Boise, investor contractor Meridian, renovation contractor Boise',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://fixrightservices.com'}/blog/fix-flip-contractor-boise-idaho`,
    ogType: 'article',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Article',
          headline: 'Fix & Flip Contractor in Boise, Idaho: What Investors Need to Know',
          datePublished: '2025-01-15',
          dateModified: '2025-01-15',
          author: {
            '@type': 'Organization',
            name: 'Fix Right Services',
          },
          publisher: {
            '@type': 'Organization',
            name: 'Fix Right Services',
            logo: {
              '@type': 'ImageObject',
              url: 'https://static.readdy.ai/fixrightservices/logo.svg',
            },
          },
          image: 'https://public.readdy.ai/ai/img_res/ce38727bc1c0dc059f77322823e94e6a.jpg',
          keywords: 'fix and flip contractor Boise Idaho, real estate investor contractor Treasure Valley, house flip contractor Boise',
        },
        {
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'What should I look for in a fix and flip contractor in Boise?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Look for a licensed Idaho contractor with fix & flip experience, proven track record with investors, transparent pricing, fast turnaround capability, knowledge of Boise market trends, and ability to work within investor budgets. Verify they understand ARV (After Repair Value) and can recommend value-add improvements.',
              },
            },
            {
              '@type': 'Question',
              name: 'How long does a typical fix and flip renovation take in Boise?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Most fix and flip renovations in Boise take 4-12 weeks depending on scope. Cosmetic flips (paint, flooring, fixtures) take 3-6 weeks. Moderate renovations (kitchen, bath, flooring) take 6-10 weeks. Full gut renovations take 10-16 weeks. Timeline is critical for investor ROI.',
              },
            },
            {
              '@type': 'Question',
              name: 'What is the average cost to flip a house in Boise, Idaho?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'The average fix and flip renovation in Boise costs $25,000-$75,000 depending on property condition and target market. Light cosmetic flips cost $15,000-$30,000, moderate renovations $30,000-$60,000, and full gut rehabs $60,000-$100,000+. Costs vary by neighborhood and ARV.',
              },
            },
            {
              '@type': 'Question',
              name: 'Do fix and flip contractors in Boise offer investor partnerships?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes, many experienced Boise contractors offer investor partnership models including deferred payment until sale, equity partnerships, or preferred contractor rates for repeat investors. Fix Right Services works with real estate investors throughout the Treasure Valley on flexible partnership terms.',
              },
            },
            {
              '@type': 'Question',
              name: 'What renovations add the most value to a flip in Boise?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'In Boise\'s market, kitchen and bathroom updates deliver the highest ROI (70-90% return). Other high-value improvements include fresh paint, new flooring, curb appeal upgrades, modern lighting, and updated fixtures. Focus on buyer expectations for the target neighborhood.',
              },
            },
            {
              '@type': 'Question',
              name: 'How do I find a reliable fix and flip contractor in Treasure Valley?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Network with local real estate investor groups (REIA), ask for referrals from successful flippers, verify Idaho contractor license and insurance, review past flip projects, check references from other investors, and ensure they understand investor timelines and budgets.',
              },
            },
          ],
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://fixrightservices.org/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Blog',
              item: 'https://fixrightservices.org/blog',
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: 'Fix & Flip Contractor in Boise, Idaho',
              item: 'https://fixrightservices.org/blog/fix-flip-contractor-boise-idaho',
            },
          ],
        },
      ],
    },
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="/" className="flex items-center gap-2 cursor-pointer">
              <img src="/images/logo.svg" alt="Fix Right Services" className="h-10 w-auto" />
            </a>
            <div className="hidden md:flex items-center gap-8">
              <a href="/#services" className="text-gray-700 hover:text-[#B45309] font-medium cursor-pointer whitespace-nowrap">
                Services
              </a>
              <a href="/blog" className="text-[#B45309] font-medium cursor-pointer whitespace-nowrap">
                Blog
              </a>
              <a href="/#contact" className="bg-[#B45309] text-white px-6 py-2.5 rounded-lg hover:bg-[#92400E] font-semibold cursor-pointer whitespace-nowrap">
                Get Free Quote
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-[#B45309]/5 to-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm mb-6">
            <a href="/" className="text-gray-500 hover:text-[#B45309] cursor-pointer">Home</a>
            <Icon name="arrow-right" className="w-3 h-3 text-gray-400" />
            <a href="/blog" className="text-gray-500 hover:text-[#B45309] cursor-pointer">Blog</a>
            <Icon name="arrow-right" className="w-3 h-3 text-gray-400" />
            <span className="text-gray-900">Fix & Flip Contractor</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Fix & Flip Contractor in Boise, Idaho: What Investors Need to Know
          </h1>
          <div className="flex items-center gap-4 text-sm text-gray-600 mb-6">
            <span className="flex items-center gap-2">
              <Icon name="calendar" className="w-4 h-4" />
              January 15, 2025
            </span>
            <span className="flex items-center gap-2">
              <Icon name="time" className="w-4 h-4" />
              9 min read
            </span>
          </div>
          <img
            src="https://readdy.ai/api/search-image?query=house%20renovation%20construction%20site%20boise%20idaho%20contractor%20working%20residential%20property%20flip%20investment%20modern%20tools%20professional%20real%20estate%20investing%20clean%20background%20high%20quality%20photography&width=1200&height=630&seq=fix-flip-hero&orientation=landscape"
            alt="Fix and Flip Contractor in Boise Idaho"
            className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
            loading="eager"
          />
        </div>
      </section>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          <p className="text-xl text-gray-700 leading-relaxed mb-8">
            Choosing the right contractor can make or break your fix and flip investment in Boise, Idaho. The Treasure Valley's hot real estate market offers excellent opportunities for investors, but success depends on finding a contractor who understands investor timelines, budgets, and ROI expectations.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Why Contractor Choice Makes or Breaks Your Flip</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            In Boise's competitive real estate market, <strong>time is money</strong>. Every week your property sits under renovation costs you in holding costs, interest, and opportunity cost. The right contractor delivers:
          </p>
          <ul className="space-y-2 text-gray-700 mb-8 ml-6">
            <li>• <strong>Fast turnaround:</strong> 4-12 week completion vs. 16-24 weeks with inexperienced contractors</li>
            <li>• <strong>Budget accuracy:</strong> Detailed estimates with minimal change orders</li>
            <li>• <strong>Market knowledge:</strong> Understanding what Boise buyers expect in each price range</li>
            <li>• <strong>Quality work:</strong> Renovations that pass inspection and attract buyers</li>
            <li>• <strong>Investor-friendly terms:</strong> Flexible payment schedules and partnership options</li>
          </ul>
          <div className="bg-[#B45309]/5 border-l-4 border-[#B45309] p-6 rounded-r-lg mb-8">
            <p className="text-gray-800 font-semibold mb-2">Real Cost of Wrong Contractor Choice:</p>
            <p className="text-gray-700">A 6-week delay on a $300,000 flip costs approximately $4,500-$6,000 in holding costs (mortgage, utilities, insurance, taxes). Poor quality work can reduce ARV by $10,000-$30,000.</p>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">What to Look for in a Fix & Flip Contractor</h2>
          
          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Proven Fix & Flip Experience</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            Not all contractors understand investor needs. Look for contractors who:
          </p>
          <ul className="space-y-2 text-gray-700 mb-6 ml-6">
            <li>• Have completed 10+ flips in the Treasure Valley</li>
            <li>• Can provide investor references (not just homeowner references)</li>
            <li>• Understand ARV (After Repair Value) and can recommend value-add improvements</li>
            <li>• Know which renovations deliver ROI and which are over-improvements</li>
          </ul>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Fast, Reliable Timeline</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            <strong>Speed matters in flipping.</strong> Your contractor should:
          </p>
          <ul className="space-y-2 text-gray-700 mb-6 ml-6">
            <li>• Provide realistic timelines with milestone dates</li>
            <li>• Have crews available to start within 1-2 weeks</li>
            <li>• Work 5-6 days per week (not just weekends)</li>
            <li>• Handle permits efficiently to avoid delays</li>
            <li>• Communicate proactively about any timeline changes</li>
          </ul>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. Transparent, Detailed Pricing</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            Investor-friendly contractors provide:
          </p>
          <ul className="space-y-2 text-gray-700 mb-6 ml-6">
            <li>• Itemized quotes broken down by room and task</li>
            <li>• Clear payment schedules tied to milestones</li>
            <li>• Upfront disclosure of potential additional costs</li>
            <li>• Competitive pricing without sacrificing quality</li>
            <li>• Volume discounts for repeat investors</li>
          </ul>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. Licensed, Insured, and Bonded</h3>
          <p className="text-gray-700 leading-relaxed mb-6">
            <strong>Non-negotiable requirements:</strong> Idaho contractor license, general liability insurance ($1M+ coverage), workers' compensation insurance, and surety bond. Verify license status at <strong>ibol.idaho.gov</strong>.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Knowledge of Boise Market Trends</h3>
          <p className="text-gray-700 leading-relaxed mb-6">
            Your contractor should understand what sells in different Boise neighborhoods. Eagle buyers expect different finishes than Nampa buyers. Meridian flips need different features than North Boise properties.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Typical Renovation Scope for Boise Flips</h2>
          
          <div className="space-y-6 mb-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <span className="bg-[#B45309] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">1</span>
                Cosmetic Flip ($15,000 - $30,000)
              </h4>
              <p className="text-sm text-gray-700 mb-2"><strong>Timeline:</strong> 3-6 weeks</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Scope:</strong></p>
              <ul className="text-sm text-gray-700 space-y-1 ml-4">
                <li>• Fresh paint (interior & exterior)</li>
                <li>• New flooring (vinyl plank or carpet)</li>
                <li>• Updated light fixtures and hardware</li>
                <li>• New appliances</li>
                <li>• Landscaping and curb appeal</li>
                <li>• Deep cleaning</li>
              </ul>
              <p className="text-sm text-gray-700 mt-2"><strong>Best for:</strong> Properties in good condition needing cosmetic updates. Common in Meridian and Nampa.</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <span className="bg-[#B45309] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">2</span>
                Moderate Renovation ($30,000 - $60,000)
              </h4>
              <p className="text-sm text-gray-700 mb-2"><strong>Timeline:</strong> 6-10 weeks</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Scope:</strong></p>
              <ul className="text-sm text-gray-700 space-y-1 ml-4">
                <li>• Kitchen remodel (cabinets, countertops, appliances)</li>
                <li>• Bathroom updates (vanity, tile, fixtures)</li>
                <li>• New flooring throughout</li>
                <li>• Interior and exterior paint</li>
                <li>• Updated electrical and plumbing fixtures</li>
                <li>• HVAC service or replacement</li>
                <li>• Landscaping</li>
              </ul>
              <p className="text-sm text-gray-700 mt-2"><strong>Best for:</strong> Most Boise flips. Delivers strong ROI in $300,000-$500,000 price range.</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <span className="bg-[#B45309] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">3</span>
                Full Gut Renovation ($60,000 - $100,000+)
              </h4>
              <p className="text-sm text-gray-700 mb-2"><strong>Timeline:</strong> 10-16 weeks</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Scope:</strong></p>
              <ul className="text-sm text-gray-700 space-y-1 ml-4">
                <li>• Complete kitchen and bathroom remodels</li>
                <li>• New electrical and plumbing systems</li>
                <li>• HVAC replacement</li>
                <li>• New roof or major roof repairs</li>
                <li>• Foundation repairs</li>
                <li>• Structural modifications</li>
                <li>• All new flooring, paint, fixtures</li>
              </ul>
              <p className="text-sm text-gray-700 mt-2"><strong>Best for:</strong> Distressed properties or high-end flips in Eagle and North Boise.</p>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Investor Partnership Models</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Many experienced Boise contractors offer <strong>flexible partnership arrangements</strong> for real estate investors:
          </p>
          
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="border-2 border-[#B45309] p-6 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-3">Deferred Payment Model</h4>
              <p className="text-sm text-gray-700 mb-3">Contractor defers partial payment until property sells. Reduces upfront capital requirements for investors.</p>
              <p className="text-sm text-gray-700"><strong>Best for:</strong> Investors with limited cash reserves or multiple simultaneous projects.</p>
            </div>
            <div className="border-2 border-[#B45309] p-6 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-3">Equity Partnership</h4>
              <p className="text-sm text-gray-700 mb-3">Contractor takes equity stake in exchange for reduced or deferred fees. Aligns contractor incentives with investor success.</p>
              <p className="text-sm text-gray-700"><strong>Best for:</strong> High-value flips or investors seeking to leverage contractor expertise.</p>
            </div>
            <div className="border-2 border-[#B45309] p-6 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-3">Volume Discount Model</h4>
              <p className="text-sm text-gray-700 mb-3">Preferred pricing for investors committing to multiple projects. Typically 10-15% discount on standard rates.</p>
              <p className="text-sm text-gray-700"><strong>Best for:</strong> Active investors flipping 3+ properties per year.</p>
            </div>
            <div className="border-2 border-[#B45309] p-6 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-3">Milestone Payment Plan</h4>
              <p className="text-sm text-gray-700 mb-3">Payment tied to completion milestones rather than time-based draws. Protects investor from paying for incomplete work.</p>
              <p className="text-sm text-gray-700"><strong>Best for:</strong> First-time flippers or investors working with new contractors.</p>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-6 mb-12">
            <div className="border-l-4 border-[#B45309] pl-6">
              <h4 className="font-bold text-gray-900 mb-2">What should I look for in a fix and flip contractor in Boise?</h4>
              <p className="text-gray-700">Look for a licensed Idaho contractor with fix & flip experience, proven track record with investors, transparent pricing, fast turnaround capability, knowledge of Boise market trends, and ability to work within investor budgets. Verify they understand ARV (After Repair Value) and can recommend value-add improvements.</p>
            </div>
            <div className="border-l-4 border-[#B45309] pl-6">
              <h4 className="font-bold text-gray-900 mb-2">How long does a typical fix and flip renovation take in Boise?</h4>
              <p className="text-gray-700">Most fix and flip renovations in Boise take 4-12 weeks depending on scope. Cosmetic flips (paint, flooring, fixtures) take 3-6 weeks. Moderate renovations (kitchen, bath, flooring) take 6-10 weeks. Full gut renovations take 10-16 weeks. Timeline is critical for investor ROI.</p>
            </div>
            <div className="border-l-4 border-[#B45309] pl-6">
              <h4 className="font-bold text-gray-900 mb-2">What is the average cost to flip a house in Boise, Idaho?</h4>
              <p className="text-gray-700">The average fix and flip renovation in Boise costs $25,000-$75,000 depending on property condition and target market. Light cosmetic flips cost $15,000-$30,000, moderate renovations $30,000-$60,000, and full gut rehabs $60,000-$100,000+. Costs vary by neighborhood and ARV.</p>
            </div>
            <div className="border-l-4 border-[#B45309] pl-6">
              <h4 className="font-bold text-gray-900 mb-2">Do fix and flip contractors in Boise offer investor partnerships?</h4>
              <p className="text-gray-700">Yes, many experienced Boise contractors offer investor partnership models including deferred payment until sale, equity partnerships, or preferred contractor rates for repeat investors. Fix Right Services works with real estate investors throughout the Treasure Valley on flexible partnership terms.</p>
            </div>
            <div className="border-l-4 border-[#B45309] pl-6">
              <h4 className="font-bold text-gray-900 mb-2">What renovations add the most value to a flip in Boise?</h4>
              <p className="text-gray-700">In Boise's market, kitchen and bathroom updates deliver the highest ROI (70-90% return). Other high-value improvements include fresh paint, new flooring, curb appeal upgrades, modern lighting, and updated fixtures. Focus on buyer expectations for the target neighborhood.</p>
            </div>
            <div className="border-l-4 border-[#B45309] pl-6">
              <h4 className="font-bold text-gray-900 mb-2">How do I find a reliable fix and flip contractor in Treasure Valley?</h4>
              <p className="text-gray-700">Network with local real estate investor groups (REIA), ask for referrals from successful flippers, verify Idaho contractor license and insurance, review past flip projects, check references from other investors, and ensure they understand investor timelines and budgets.</p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-[#B45309] to-[#92400E] text-white p-8 rounded-xl mb-12">
            <h3 className="text-2xl font-bold mb-3">Partner with Fix Right Services</h3>
            <p className="mb-6 text-white/90">We specialize in fix and flip renovations for real estate investors throughout the Treasure Valley. Fast timelines, transparent pricing, and flexible partnership options.</p>
            <a href="/#contact" className="inline-flex items-center gap-2 bg-white text-[#B45309] px-8 py-3 rounded-lg font-bold hover:bg-gray-100 cursor-pointer whitespace-nowrap">
              Discuss Your Project
              <Icon name="arrow-right" className="w-4 h-4" />
            </a>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Related Articles</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <a href="/blog/fix-flip-roi-boise-renovations" className="block bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-shadow cursor-pointer">
              <h4 className="font-bold text-gray-900 mb-2">Fix & Flip ROI: Which Renovations Add the Most Value in Boise</h4>
              <p className="text-sm text-gray-600 mb-3">Data-driven analysis of which renovation investments deliver the highest returns in Treasure Valley flips.</p>
              <span className="text-[#B45309] font-semibold text-sm flex items-center gap-2">
                Read More
                <Icon name="arrow-right" className="w-3.5 h-3.5" />
              </span>
            </a>
            <a href="/blog/fix-flip-timeline-idaho" className="block bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-shadow cursor-pointer">
              <h4 className="font-bold text-gray-900 mb-2">Fix & Flip Timeline: How Long Does a Renovation Take in Idaho</h4>
              <p className="text-sm text-gray-600 mb-3">Complete timeline breakdown from purchase to sale for Boise fix and flip projects.</p>
              <span className="text-[#B45309] font-semibold text-sm flex items-center gap-2">
                Read More
                <Icon name="arrow-right" className="w-3.5 h-3.5" />
              </span>
            </a>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-[#1F2937] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <img src="/images/logo.svg" alt="Fix Right Services" className="h-10 mb-4 brightness-0 invert" />
              <p className="text-gray-300 text-sm">Professional fix & flip contractor serving real estate investors in Boise, Idaho.</p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Services</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li><a href="/#services" className="hover:text-white cursor-pointer">Fix & Flip Renovations</a></li>
                <li><a href="/#services" className="hover:text-white cursor-pointer">Investor Partnerships</a></li>
                <li><a href="/#services" className="hover:text-white cursor-pointer">Property Renovations</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li><a href="/blog" className="hover:text-white cursor-pointer">Blog</a></li>
                <li><a href="/#contact" className="hover:text-white cursor-pointer">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Contact</h4>
              <p className="text-sm text-gray-300 mb-2">Boise, Idaho</p>
              <p className="text-sm text-gray-300">(208) 123-4567</p>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2025 Fix Right Services. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}